// ============================================================================
// PERIOD LOCK SERVICE — close/lock a date range (ADR-0016). Entity-scoped.
// ============================================================================

import { get, post, del } from '../client/axiosClient';
import { periodLockEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  PeriodLockDto,
  CreatePeriodLockRequest,
  PeriodLockQueryParams,
} from '../types';

class PeriodLockService {
  async getAll(params?: PeriodLockQueryParams): Promise<PaginatedResponse<PeriodLockDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(periodLockEndpoints.list(), params)
      : periodLockEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<PeriodLockDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async create(data: CreatePeriodLockRequest): Promise<PeriodLockDto> {
    const response = await post<ApiResponse<PeriodLockDto>>(periodLockEndpoints.create(), data);
    return response.data.data;
  }

  /** Unlock = soft-deactivate the lock; rowVersion guards concurrency. */
  async unlock(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(periodLockEndpoints.unlock(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }
}

export const periodLockService = new PeriodLockService();
export default periodLockService;
